import { useCallback, useEffect, useState } from 'react';
import { AppShell } from '../../components/AppShell';
import { Button } from '../../components/Button';
import { Field, inputStyle } from '../../components/Field';
import { Spinner } from '../../components/Spinner';
import { apiGet, apiSend, ClientApiError } from '../../api/client';
import { formatPhone } from '../../lib/format';
import type { FieldErrors } from '../../../shared/validation';

interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  phone: string | null;
  role: string;
  status: string;
  rejectionReason: string | null;
}

export function UsersScreen() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    role: 'tech',
  });
  const [formMustChange, setFormMustChange] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  // Which user card has the set-password form open, and its inputs.
  const [resetFor, setResetFor] = useState<string | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetMustChange, setResetMustChange] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await apiGet<{ users: AdminUser[] }>('/api/users');
      setUsers(data.users);
    } catch (caught) {
      setError(caught instanceof ClientApiError ? caught.message : 'Could not load users.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(userId: string, body: unknown): Promise<boolean> {
    setBusy(true);
    setError('');
    try {
      await apiSend(`/api/users/${userId}`, 'PATCH', body);
      await load();
      return true;
    } catch (caught) {
      setError(caught instanceof ClientApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function createUser(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setFieldErrors({});
    try {
      await apiSend('/api/users', 'POST', { ...form, mustChangePassword: formMustChange });
      setForm({ fullName: '', email: '', phone: '', password: '', role: 'tech' });
      setFormMustChange(false);
      setFieldErrors({});
      setShowCreate(false);
      await load();
    } catch (caught) {
      if (caught instanceof ClientApiError && caught.fields) {
        setFieldErrors(caught.fields);
      } else {
        setError(caught instanceof ClientApiError ? caught.message : 'Could not create that user.');
      }
    } finally {
      setBusy(false);
    }
  }

  const pending = users?.filter((u) => u.status === 'pending') ?? [];
  const rest = users?.filter((u) => u.status !== 'pending') ?? [];

  return (
    <AppShell title="Users">
      {error && <div className="error-banner">{error}</div>}
      {!users && <Spinner label="Loading users" />}

      {pending.length > 0 && (
        <>
          <h2>Waiting for approval</h2>
          {pending.map((user) => (
            <div key={user.id} className="card">
              <strong>{user.fullName}</strong>
              <p className="muted" style={{ margin: '0.2rem 0' }}>
                {user.email} · {user.phone ? formatPhone(user.phone) : 'No phone'}
              </p>
              <div className="row">
                <Button disabled={busy} onClick={() => void act(user.id, { action: 'approve' })}>
                  Approve
                </Button>
                <Button
                  variant="danger"
                  disabled={busy}
                  onClick={() => {
                    // Cancel aborts the reject entirely; OK with an empty
                    // field rejects without a reason. The reason, when given,
                    // is shown to the plumber at their next sign-in attempt.
                    const reason = window.prompt(
                      'Reason for rejecting (optional). The plumber will see this. Cancel to keep them pending.',
                    );
                    if (reason !== null) {
                      void act(user.id, { action: 'reject', reason: reason.trim() });
                    }
                  }}
                >
                  Reject
                </Button>
              </div>
            </div>
          ))}
        </>
      )}

      <div className="row" style={{ justifyContent: 'space-between', marginTop: '1rem' }}>
        <h2 style={{ margin: 0 }}>All accounts</h2>
        <Button
          variant="secondary"
          onClick={() => {
            setShowCreate((prev) => !prev);
            setFieldErrors({});
            setError('');
          }}
        >
          {showCreate ? 'Cancel' : 'Add internal user'}
        </Button>
      </div>

      {showCreate && (
        <form className="card" onSubmit={createUser}>
          <Field label="Full name" htmlFor="new-name" error={fieldErrors.fullName}>
            <input
              id="new-name"
              style={inputStyle}
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            />
          </Field>
          <Field label="Email" htmlFor="new-email" error={fieldErrors.email}>
            <input
              id="new-email"
              type="email"
              style={inputStyle}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
          <Field label="Phone" htmlFor="new-phone" error={fieldErrors.phone}>
            <input
              id="new-phone"
              type="tel"
              style={inputStyle}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </Field>
          <Field label="Role" htmlFor="new-role">
            <select
              id="new-role"
              style={inputStyle}
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              <option value="tech">Field Tech</option>
              <option value="recon">Recon</option>
              <option value="dispatch">Dispatch</option>
            </select>
          </Field>
          <Field
            label="Password"
            htmlFor="new-password"
            hint="At least 12 characters. This is their password until they change it."
            error={fieldErrors.password}
          >
            <input
              id="new-password"
              style={inputStyle}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </Field>
          <label
            style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minHeight: 'var(--tap)' }}
          >
            <input
              type="checkbox"
              checked={formMustChange}
              onChange={(e) => setFormMustChange(e.target.checked)}
              style={{ width: 20, height: 20 }}
            />
            Require a password change at next sign-in
          </label>
          <Button type="submit" full disabled={busy}>
            Create user
          </Button>
        </form>
      )}

      {rest.map((user) => (
        <div key={user.id} className="card">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <strong>{user.fullName}</strong>
            <span className="muted">
              {user.role} · {user.status}
            </span>
          </div>
          {user.rejectionReason && (
            <p className="muted" style={{ margin: '0.2rem 0', color: 'var(--danger)' }}>
              Rejected: {user.rejectionReason}
            </p>
          )}
          <p className="muted" style={{ margin: '0.2rem 0' }}>
            {user.email}
          </p>
          <div className="row">
            {user.status === 'active' ? (
              <Button
                variant="danger"
                disabled={busy}
                onClick={() => void act(user.id, { action: 'disable' })}
              >
                Disable
              </Button>
            ) : (
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => void act(user.id, { action: 'enable' })}
              >
                Enable
              </Button>
            )}
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setResetFor(resetFor === user.id ? null : user.id);
                setResetPassword('');
                setResetMustChange(false);
              }}
            >
              {resetFor === user.id ? 'Cancel' : 'Set password'}
            </Button>
          </div>

          {resetFor === user.id && (
            <div style={{ marginTop: '0.75rem', borderTop: '1px solid var(--line)', paddingTop: '0.75rem' }}>
              <Field
                label="New password"
                htmlFor={`set-pw-${user.id}`}
                hint="At least 12 characters. Signs them out everywhere. Give it to them directly."
              >
                <input
                  id={`set-pw-${user.id}`}
                  style={inputStyle}
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                />
              </Field>
              <label
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center',
                  minHeight: 'var(--tap)',
                }}
              >
                <input
                  type="checkbox"
                  checked={resetMustChange}
                  onChange={(e) => setResetMustChange(e.target.checked)}
                  style={{ width: 20, height: 20 }}
                />
                Require a password change at next sign-in
              </label>
              <Button
                full
                disabled={busy || resetPassword.length < 12}
                onClick={async () => {
                  const done = await act(user.id, {
                    action: 'reset_password',
                    temporaryPassword: resetPassword,
                    mustChangePassword: resetMustChange,
                  });
                  if (done) {
                    setResetFor(null);
                    setResetPassword('');
                    setResetMustChange(false);
                  }
                }}
              >
                Save password
              </Button>
            </div>
          )}
        </div>
      ))}
    </AppShell>
  );
}
